import React from 'react';

/**
 * Founding Creator Badge — holographic cyan → magenta gradient pill.
 *
 * Shown next to the existing tier badge wherever a creator is surfaced
 * (profile, video card, Pulse, fullscreen player).
 *
 * Props:
 *   - number: integer 1..50 (FOUNDING_CREATORS_MAX). If falsy, returns null.
 *   - size:   'sm' (video cards, Pulse), 'md' (profile header). Default 'sm'.
 *   - className: extra Tailwind classes
 */
export const FoundingCreatorBadge = ({ number, size = 'sm', className = '' }) => {
  if (!number) return null;
  const padded = String(number).padStart(2, '0');
  const isMd = size === 'md';

  // Holographic gradient — cyan → violet → magenta with an animated subtle
  // shimmer overlay. Pure CSS, no extra deps.
  return (
    <span
      className={`relative inline-flex items-center gap-1 rounded-full font-bold tracking-wide overflow-hidden ${
        isMd ? 'px-3 py-1 text-xs' : 'px-2 py-0.5 text-[10px]'
      } ${className}`}
      style={{
        background:
          'linear-gradient(110deg, #22d3ee 0%, #a78bfa 45%, #ec4899 100%)',
        color: '#0a0a0a',
        boxShadow:
          '0 0 0 1px rgba(255,255,255,0.25) inset, 0 4px 14px rgba(168,139,250,0.35)',
      }}
      title={`Founding Creator #${padded} — one of the first ${50} AUDIVUE artists`}
      data-testid={`founding-creator-badge-${padded}`}
    >
      <span
        aria-hidden
        className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-50"
        style={{
          background:
            'linear-gradient(110deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0) 100%)',
          backgroundSize: '200% 100%',
          animation: 'founding-shimmer 3.5s linear infinite',
        }}
      />
      <svg
        width={isMd ? '12' : '10'}
        height={isMd ? '12' : '10'}
        viewBox="0 0 24 24"
        fill="currentColor"
        className="relative"
        aria-hidden
      >
        <path d="M12 2l2.39 7.36H22l-6.18 4.49L18.21 22 12 17.27 5.79 22l2.39-8.15L2 9.36h7.61z" />
      </svg>
      <span className="relative">
        {isMd ? `Founding Creator #${padded}` : `#${padded}`}
      </span>
    </span>
  );
};

export default FoundingCreatorBadge;
